// SelectBatchModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { FiSearch } from "react-icons/fi";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useLazySearchBatchQuery, useAddNewBatchMutation } from "../../State/Api/PharmacyStockApiSlice";
import { FaChevronRight } from "react-icons/fa";
// If your slice exports different hook names, replace the imports above.

const thStyle = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#555",
  borderBottom: "1px solid #ddd",
};
const tdStyle = {
  padding: "10px 14px",
  fontSize: "13px",
  color: "#232224",
  verticalAlign: "middle",
};
const emptyStyle = { textAlign: "center", padding: "18px", color: "#777" };

export default function SelectBatchModal({
  isOpen,
  onClose,
  productName, // optional contextual product name
  onSelectBatch, // (batchObj) => void
  batchFormik,
  productId
}) {
  console.log("productId:", productId);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]); // array of objects
  const [selectedRow, setSelectedRow] = useState(null);

  // RTK lazy query + mutation - adjust hook names if your slice uses different names
  const [triggerSearch, searchResult] = useLazySearchBatchQuery();
  const [addBatch, addResult] = useAddNewBatchMutation();

  const { data: searchData, isFetching: searching, isError: searchError, error: searchErrorObj } = searchResult;
  const { isLoading: adding, isSuccess: addSuccess, error: addError } = addResult;

  const debounceRef = useRef(null);

  useEffect(() => {
    // normalize API result -> results[] whenever searchData changes
    if (!searchData) {
      setResults([]);
      return;
    }

    // If the server returns an array or wrapped object, try a few shapes:
    let list = [];
    if (Array.isArray(searchData)) list = searchData;
    else if (Array.isArray(searchData.data)) list = searchData.data;
    else if (Array.isArray(searchData.result)) list = searchData.result;
    else if (Array.isArray(searchData.batches)) list = searchData.batches;
    else if (typeof searchData === "object") {
      // sometimes server returns single item; wrap it
      list = Object.values(searchData).flat().filter(Boolean);
      if (!list.length && Object.keys(searchData).length > 0) {
        // fallback: treat the top-level object as single result
        list = [searchData];
      }
    }

    setResults(list);
  }, [searchData]);

  // call lazy query while user types (debounced)
useEffect(() => {
  const pharmaId = localStorage.getItem("userUniqueID") || "";

  console.log(productId);
  if (!search || !search.trim()) {
    setResults([]);
    return;
  }

  const id = setTimeout(async () => {
    try {
      // call trigger and unwrap to get actual payload
      const payload = await triggerSearch({ pharmaId, productid: batchFormik.values.ProductId ?? productId, batch: search.trim() }).unwrap();
      // normalize payload -> array
      let list = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload.data)) list = payload.data;
      else if (Array.isArray(payload.result)) list = payload.result;
      else if (Array.isArray(payload.batches)) list = payload.batches;
      else if (typeof payload === "object") {
        list = Object.values(payload).flat().filter(Boolean);
        if (!list.length && Object.keys(payload).length > 0) list = [payload];
      }
      setResults(list);
    } catch (err) {
      console.error("search failed", err);
      setResults([]);
    }
  }, 300);

  return () => clearTimeout(id);
}, [search, triggerSearch]);


  // handle add batch button click
  const handleAddBatch = async () => {
    try {
      const pharmaId = localStorage.getItem("userUniqueID") || "";
      const payload = {
        pharmaId,
        searchTextBatchNumber: search.trim(),
        productId: batchFormik.values.ProductId ?? productId,
        createdBy: pharmaId,
      };
      const created = await addBatch(payload).unwrap();

      onSelectBatch?.(search.trim());
      
      onClose?.();
    } catch (err) {
      console.error("Failed to add batch:", err);
      // optionally show toast / set local error state
    }
  };

  const handleRowClick = (row, idx) => {
    setSelectedRow(idx);
    onSelectBatch?.(row?.batchNo);
    onClose?.();
  };

  if (!isOpen) return null;

  // Decide whether we have results to show
  const hasResults = Array.isArray(results) && results.length > 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 820,
          maxHeight: "80vh",
          background: "#fff",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Select Batch</div>
            <div style={{ fontSize: 12, color: "#666" }}>{productName ? `Product: ${productName}` : "Search batch numbers"}</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {/* search */}
        <div style={{ padding: 14, borderBottom: "1px solid #f1f1f1", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", width: 380 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type batch number to search..."
              style={{ width: "100%", padding: "8px 36px 8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }}
            />
            <FiSearch style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
          </div>

          <div style={{ fontSize: 13, color: "#666" }}>
            {searching ? "Searching..." : search ? `${results.length} result(s)` : "Start typing to search"}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => { setSearch(""); setResults([]); }}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer" }}
            >
              Clear
            </button>
          </div>
        </div>

        {/* table */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={thStyle}>BATCH NUMBER</th>
                <th style={thStyle}>PHARMA ID</th>
                <th style={thStyle}>STATUS</th>
                <th style={thStyle}></th>
              </tr>
            </thead>

            <tbody>
              {searchError && (
                <tr>
                  <td colSpan="4" style={emptyStyle}>
                    <span style={{ color: "#666" }}>No results matching your search</span>
                  </td>
                </tr>
              )}

              {hasResults ? (
                results.map((r, idx) => {
                  // r may be in any shape; show properties safely
                  const batchNo = r.searchTextBatchNumber ?? r.batchNo ?? r.batchNumber ?? r?.searchText ?? "N/A";
                  const pharmaId = r.pharmaID ?? r?.pharmaID ?? "N/A";
                  const status = r.status ?? r.status ?? "N/A";

                  return (
                    <tr
                      key={idx}
                      onClick={() => handleRowClick(r, idx)}
                      style={{
                        cursor: "pointer",
                        background: selectedRow === idx ? "#f0fff4" : idx % 2 === 0 ? "#fff" : "#fbfbfb",
                      }}
                    >
                      <td style={tdStyle}>{batchNo || "N/A"}</td>
                      <td style={tdStyle}>{pharmaId || "N/A"}</td>
                      <td style={tdStyle}>{status || "N/A"}</td>
                      <td style={{ ...tdStyle, fontSize: 18, textAlign: "center" }}>
                        <MdOutlineKeyboardArrowRight />
                      </td>
                    </tr>
                  );
                })
              ) : (
                // when there are no results (and user typed something) show "Add batch" row
                <tr>
                  <td colSpan="4" style={emptyStyle}>
                    {search.trim() ? (
                      <div className="bg-gray-200" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
                        
                        <button
                          onClick={handleAddBatch}
                          disabled={adding}
                          style={{
                            padding: "8px 12px",
                            background: "transparent",
                            color: "black",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {adding ? "Adding..." : `Add batch "${search}"`}
                          <FaChevronRight style={{ marginLeft: 6 }} />
                        </button>
                      </div>
                    ) : (
                      <div>Start typing a batch number to search</div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
